
import React from 'react';
import { Link } from 'react-router-dom';
import { Users, HeartHandshake, ArrowRight } from 'lucide-react';
import FadeIn from '../../../components/FadeIn';
import SEO from '../../../components/SEO';

const StakeholderManagement = () => {
    return (
        <div className="bg-white">
            <SEO
                title="Stakeholder Management Training | BPO Consultants"
                description="Learn to identify and influence the buying committee. Turn blockers into champions and secure consensus."
                keywords="Stakeholder Management, Buying Committee, Sales Champion Building, Enterprise Sales Training, Decision Maker Influence"
            />

            {/* Hero */}
            <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-[#0B0D15] overflow-hidden">
                <div className="absolute inset-0 opacity-20">
                    <img src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&q=80&w=1200" alt="Meeting" className="w-full h-full object-cover" />
                </div>
                <div className="max-w-7xl mx-auto relative z-10 text-center">
                    <FadeIn>
                        <span className="inline-block bg-[#A2CB52]/20 text-[#A2CB52] px-4 py-2 rounded-full text-sm font-bold border border-[#A2CB52]/40 mb-6 backdrop-blur-sm">
                            <Users size={14} className="inline mr-1" /> Module 2: Influence
                        </span>
                        <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
                            Stakeholder <br /> Management
                        </h1>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                            Win the room. Influence the people who influence the decision.
                        </p>
                    </FadeIn>
                </div>
            </section>

            <section className="py-20 px-4 sm:px-6 lg:px-8">
                <div className="max-w-4xl mx-auto">
                    <FadeIn className="mb-16">
                        <h2 className="text-3xl font-bold text-[#20222D] mb-6">The Art of Consensus</h2>
                        <p className="text-lg text-gray-600 leading-relaxed mb-8">
                            The average B2B deal now involves 6-10 decision makers. If you only talk to one, you lose. Learn to multi-thread your deals and build a coalition of support.
                        </p>

                        <div className="grid md:grid-cols-2 gap-8">
                            {[
                                {
                                    title: "Building Champions",
                                    desc: "How to identify and arm an internal advocate who will sell for you when you're not in the room."
                                },
                                {
                                    title: "Neutralizing Blockers",
                                    desc: "Strategies for handling 'Gatekeepers' and 'Resisters' without alienating them."
                                },
                                {
                                    title: "Executive Presence",
                                    desc: "Speaking the language of the C-Suite: ROI, Risk Mitigation, and Strategic Alignment."
                                },
                                {
                                    title: "Multi-Threading Conversations",
                                    desc: "Running parallel sales tracks with Finance, IT, and Operations simultaneously."
                                }
                            ].map((item, i) => (
                                <div key={i} className="bg-gray-50 p-6 rounded-xl border border-gray-100">
                                    <div className="text-[#A2CB52] mb-4">
                                        <HeartHandshake size={28} />
                                    </div>
                                    <h3 className="text-xl font-bold text-[#20222D] mb-2">{item.title}</h3>
                                    <p className="text-gray-600 text-sm">{item.desc}</p>
                                </div>
                            ))}
                        </div>
                    </FadeIn>

                    <FadeIn className="mb-16 bg-gradient-to-r from-gray-900 to-gray-800 p-8 rounded-2xl text-white">
                        <h3 className="text-2xl font-bold mb-4">The Challenger Sale</h3>
                        <p className="text-gray-300 italic mb-4">
                            "We teach you not just to answer questions, but to reframe the customer's problem. Stakeholders respect sales reps who teach them something new about their business."
                        </p>
                    </FadeIn>

                    <FadeIn className="bg-[#20222D] rounded-3xl p-12 text-center relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-[#A2CB52] blur-[100px] opacity-20 pointer-events-none"></div>
                        <h2 className="text-3xl font-bold text-white mb-6">Lead the Deal</h2>
                        <Link to="/contact" className="inline-flex items-center bg-[#A2CB52] text-[#20222D] px-8 py-4 rounded-xl font-bold text-lg hover:bg-[#B5D575] transition-all transform hover:scale-105">
                            Start Module <ArrowRight className="ml-2" size={20} />
                        </Link>
                    </FadeIn>
                </div>
            </section>
        </div>
    );
};

export default StakeholderManagement;
